import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useEffect, useState } from 'react'

import { employersEmployerSettings } from 'rails_routes'
import {
  useGetEmployersEmployeesDuplicates,
  usePostEmployersEmployeesDuplicates
} from 'utils/web_api'
import { type EmployersEmployee } from 'utils/web_api.schemas'

import { CompletionDialog } from './CompletionDialog'
import { EmployeeSearchCell } from './EmployeeSearchCell'
import { MergeActionCell } from './MergeActionCell'
import { StatusBadge } from './StatusBadge'
import { type MergeState } from './types'
import TanstackTable, { type PaginationMetadata } from '../reusable/table/TanstackTable'

const columnHelper = createColumnHelper<EmployersEmployee>()

export function EmployeesDuplicatesTableImpl () {
  const [mergeStates, setMergeStates] = useState<MergeState>({})
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 10,
  })
  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata | undefined>()
  const [showCompletionDialog, setShowCompletionDialog] = useState(false)
  const [totalDuplicatesCount, setTotalDuplicatesCount] = useState<number | null>(null)

  // Fetch duplicate employees using the generated hook with pagination
  const { data: duplicatesData, isLoading } = useGetEmployersEmployeesDuplicates({
    'page[number]': pagination.pageIndex,
    'page[size]': pagination.pageSize,
  })
  const duplicateEmployees = duplicatesData?.data || []

  // Update pagination metadata when data changes
  useEffect(() => {
    if (duplicatesData) {
      setPaginationMetadata(duplicatesData.pagination)
      // Set the total count on first load
      if (totalDuplicatesCount === null && duplicatesData.pagination?.total_records) {
        setTotalDuplicatesCount(duplicatesData.pagination.total_records)
      }
    }
  }, [duplicatesData?.pagination, totalDuplicatesCount])

  // Auto-redirect to last valid page if current page exceeds total pages
  useEffect(() => {
    if (paginationMetadata?.total_pages && pagination.pageIndex > paginationMetadata.total_pages) {
      setPagination(prev => ({
        ...prev,
        pageIndex: paginationMetadata.total_pages,
      }))
    }
  }, [paginationMetadata?.total_pages, pagination.pageIndex])

  // Check for completion when merge states change
  useEffect(() => {
    if (totalDuplicatesCount !== null) {
      const mergedCount = Object.values(mergeStates).filter(state => state?.isMerged).length
      if (mergedCount >= totalDuplicatesCount) {
        setShowCompletionDialog(true)
      }
    }
  }, [mergeStates, totalDuplicatesCount])

  // Hook for merging employees
  const mergeMutation = usePostEmployersEmployeesDuplicates({
    mutation: {
      onSuccess: (mergedEmployee, variables) => {
        const duplicateEmployeeId = variables.data.manual_employee_id
        setMergeStates(prev => ({
          ...prev,
          [duplicateEmployeeId]: {
            ...prev[duplicateEmployeeId],
            isMerged: true,
            isPending: false,
            mergedEmployee,
          },
        }))
      },
      onError: (_error, variables) => {
        const duplicateEmployeeId = variables.data.manual_employee_id
        setMergeStates(prev => ({
          ...prev,
          [duplicateEmployeeId]: {
            ...prev[duplicateEmployeeId],
            isPending: false,
          },
        }))
      },
    },
  })

  const handleMerge = (selectedEmployee: EmployersEmployee, duplicateEmployee: EmployersEmployee) => {
    mergeMutation.mutate({
      data: {
        payroll_employee_id: selectedEmployee.id,
        manual_employee_id: duplicateEmployee.id,
      },
    })
  }

  const columns = [
    columnHelper.display({
      id: 'duplicate_employee',
      header: 'Invited Employee',
      cell: ({ row }) => {
        const employee = row.original
        const mergeState = mergeStates[employee.id]
        const isMerged = mergeState?.isMerged || false
        const mergedEmployee = mergeState?.mergedEmployee

        // If merged, show the canonical employee data with grayed-out styling
        if (isMerged && mergedEmployee) {
          return (
            <div className='py-2 pr-2 bg-gray-50 w-96 h-16 flex items-center opacity-50'>
              <div className='flex flex-col gap-y-1 w-full'>
                <div className='flex items-center gap-2'>
                  <span className='text-md font-medium'>
                    {mergedEmployee.first_name} {mergedEmployee.last_name}
                  </span>
                  <StatusBadge employee={mergedEmployee} />
                </div>
                <span className='text-xs text-gray-500'>{mergedEmployee.work_email}</span>
              </div>
            </div>
          )
        }

        // Default state - show original duplicate employee
        return (
          <div className='py-2 pr-2 bg-white w-96 h-16 flex items-center'>
            <div className='flex flex-col gap-y-1 w-full'>
              <div className='flex items-center gap-2'>
                <span className='text-md font-medium'>
                  {!employee.first_name || !employee.last_name
                    ? (
                      <span className='text-gray-500 italic'>Name missing</span>
                      )
                    : (
                    `${employee.first_name} ${employee.last_name}`
                      )}
                </span>
                <StatusBadge employee={employee} />
              </div>
              <span className='text-xs text-gray-500'>{employee.work_email}</span>
            </div>
          </div>
        )
      },
    }),
    columnHelper.display({
      id: 'employee_search',
      header: 'Payroll Employee',
      cell: ({ row }) => (
        <div className='w-96 h-16 flex items-center'>
          <div className='w-full'>
            <EmployeeSearchCell
              duplicateEmployee={row.original}
              mergeStates={mergeStates}
              setMergeStates={setMergeStates}
            />
          </div>
        </div>
      ),
    }),
    columnHelper.display({
      id: 'merge_action',
      header: '',
      cell: ({ row }) => (
        <div className='w-24 h-16 flex items-center'>
          <div className='w-full'>
            <MergeActionCell
              duplicateEmployee={row.original}
              mergeStates={mergeStates}
              setMergeStates={setMergeStates}
              onMerge={handleMerge}
            />
          </div>
        </div>
      ),
    }),
  ]

  const table = useReactTable({
    columns,
    data: duplicateEmployees,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    manualPagination: true,
    pageCount: duplicatesData?.pagination?.total_pages,
    onPaginationChange: setPagination,
    rowCount: duplicatesData?.pagination?.total_records,
    state: {
      pagination,
    },
  })

  const getNoDataMessage = () => {
    return (
      <>
        All invited employees have been matched. You can enable payroll deduction automation on your <a href={employersEmployerSettings.show({ query: { tab: 'payroll' } })}>Settings page</a>
      </>
    )
  }

  return (
    <div className='space-y-6'>
      <TanstackTable
        table={table}
        isLoading={isLoading}
        noDataMessage={getNoDataMessage()}
        paginationLabelSingular='duplicate employee'
        paginationMetadata={paginationMetadata}
        onNextPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.next_page || 1,
          })}
        onPreviousPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.prev_page || 1,
          })}
      />
      <CompletionDialog
        open={showCompletionDialog}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setShowCompletionDialog(false)
          }
        }}
      />
    </div>
  )
}
